import { Component, Inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { JobTitle, NewJobTitle } from '../../../../interfaces/job-title.interface';

export interface JobTitleDialogData {
  editingJobTitle?: JobTitle | null;
  formData: NewJobTitle;
  isSubmitting?: boolean;
}

export interface JobTitleDialogResult {
  action: 'save' | 'delete' | 'cancel';
  data?: NewJobTitle;
}

@Component({
  selector: 'app-job-title-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule
],
  template: `
    <div class="job-title-dialog crm-dialog">
      <h2 mat-dialog-title>
        <mat-icon>{{ data.editingJobTitle ? 'edit' : 'work' }}</mat-icon>
        {{ data.editingJobTitle ? 'Munkakör szerkesztése' : 'Új munkakör hozzáadása' }}
      </h2>
    
      <mat-dialog-content>
        <form class="job-title-form">
          <!-- Magyar megnevezés -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Magyar megnevezés</mat-label>
            <input matInput
              [(ngModel)]="formData.title_name_hu"
              name="titleHu"
              required
              placeholder="pl. Ügyfélmenedzser"
              [disabled]="isSubmitting">
            <mat-icon matSuffix>language</mat-icon>
          </mat-form-field>
    
          <!-- Angol megnevezés -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Angol megnevezés</mat-label>
            <input matInput
              [(ngModel)]="formData.title_name_en"
              name="titleEn"
              required
              placeholder="pl. Account Manager"
              [disabled]="isSubmitting">
            <mat-icon matSuffix>translate</mat-icon>
          </mat-form-field>
    
          <!-- Role Key -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Role Key (jogosultsági azonosító)</mat-label>
            <input matInput
              [(ngModel)]="formData.role_key"
              name="roleKey"
              required
              placeholder="pl. account_manager"
              [disabled]="isSubmitting">
            <mat-icon matSuffix>key</mat-icon>
          </mat-form-field>
    
          <!-- Aktív státusz -->
          <div class="checkbox-field">
            <mat-checkbox [(ngModel)]="formData.is_active"
              name="isActive"
              [disabled]="isSubmitting">
              Aktív munkakör
            </mat-checkbox>
          </div>
        </form>
      </mat-dialog-content>
    
      <mat-dialog-actions align="end">
        <!-- Törlés gomb (csak szerkesztés esetén) -->
        @if (data.editingJobTitle) {
          <button
            mat-button
            color="warn"
            (click)="onDelete()"
            [disabled]="isSubmitting"
            class="delete-button">
            <mat-icon>delete</mat-icon>
            Törlés
          </button>
        }
    
        <div class="spacer"></div>
    
        <!-- Mégse gomb -->
        <button mat-button (click)="onCancel()" [disabled]="isSubmitting">
          Mégse
        </button>
    
        <!-- Mentés gomb -->
        <button mat-raised-button
          color="primary"
          (click)="onSave()"
          [disabled]="!isFormValid() || isSubmitting">
          @if (isSubmitting) {
            <mat-spinner diameter="20"></mat-spinner>
          }
          @if (!isSubmitting) {
            <mat-icon>{{ data.editingJobTitle ? 'save' : 'add' }}</mat-icon>
          }
          {{ isSubmitting ? 'Mentés...' : (data.editingJobTitle ? 'Frissítés' : 'Létrehozás') }}
        </button>
      </mat-dialog-actions>
    </div>
    `,
  styles: [`
    .job-title-dialog {
      min-width: 500px;
      max-width: 600px;
    }

    .job-title-form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .full-width {
      width: 100%;
    }

    .checkbox-field {
      margin: 1rem 0;
    }

    .delete-button {
      margin-right: auto;
    }

    .spacer {
      flex: 1;
    }

    mat-dialog-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    /* Mobile responsive */
    @media (max-width: 600px) {
      .job-title-dialog {
        min-width: 300px;
        max-width: 95vw;
      }
    }
  `]
})
export class JobTitleDialog {
  formData: NewJobTitle;
  isSubmitting: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<JobTitleDialog>,
    @Inject(MAT_DIALOG_DATA) public data: JobTitleDialogData
  ) {
    // Másold át a form adatokat, hogy ne mutassunk a külső objektumra
    this.formData = { ...data.formData };
    this.isSubmitting = data.isSubmitting || false;
  }

  isFormValid(): boolean {
    return this.formData.title_name_hu.trim().length > 0 &&
           this.formData.title_name_en.trim().length > 0 &&
           this.formData.role_key.trim().length > 0;
  }

  onSave(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.dialogRef.close({
      action: 'save',
      data: this.formData
    } as JobTitleDialogResult);
  }

  onDelete(): void {
    this.dialogRef.close({
      action: 'delete'
    } as JobTitleDialogResult);
  }

  onCancel(): void {
    this.dialogRef.close({
      action: 'cancel'
    } as JobTitleDialogResult);
  }
}
